import React from 'react';
import { TimeSeriesChart } from '../charts/TimeSeriesChart';
import { TimeSeriesTypeToggle } from '../charts/TimeSeriesTypeToggle';
import { Card, CardContent, CardHeader, CardDescription, CardTitle } from '../ui/Card';
import { formatMoney } from '../../lib/formatMoney';
import { COMPACT_CHART_HEIGHT } from './panelSizes';
import { useTheme } from '../../context/ThemeContext';
import { usePersistedState } from '../../hooks/usePersistedState';

function SkeletonChart({ compact = false }) {
  return (
    <div className={`${compact ? COMPACT_CHART_HEIGHT : 'h-[260px]'} w-full animate-pulse rounded bg-slate-200`} />
  );
}

function EmptyState({ compact = false }) {
  return (
    <div className={`flex ${compact ? COMPACT_CHART_HEIGHT : 'h-[260px]'} items-center justify-center text-sm text-slate-400`}>
      No AOV data found for this period
    </div>
  );
}

export function AovTrendPanel({ aovByDate = [], currencyCode = 'DKK', loading = false, error, variant = 'detailed' }) {
  const { chartLine } = useTheme();
  const [seriesType, setSeriesType] = usePersistedState('vi.chart.aovSeriesType.v2', 'column');
  const isCompact = variant === 'compact';
  const chartHeight = isCompact ? 210 : 260;

  const rawPoints = (aovByDate || [])
    .filter((item) => item.date)
    .map((item) => ({
      date: item.date,
      value: item.averageOrderValue || 0,
      averageOrderValue: item.averageOrderValue || 0,
      invoiceCount: item.invoiceCount || 0,
    }))
    .sort((a, b) => a.date.localeCompare(b.date));

  const avgAov = rawPoints.length
    ? Math.round(rawPoints.reduce((s, d) => s + d.value, 0) / rawPoints.length)
    : 0;

  const summaryItems = [
    {
      label: 'Avg AOV',
      value: formatMoney(avgAov, { currency: currencyCode }),
      detail: `${rawPoints.length} days`,
    },
    {
      label: 'Invoice days',
      value: `${rawPoints.reduce((s, d) => s + d.invoiceCount, 0)} invoices`,
      detail: '',
    },
  ];

  return (
    <Card className="flex h-full w-full min-h-0 flex-col overflow-hidden border-slate-300">
      <CardHeader className="px-3 py-2.5">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 space-y-1 text-left">
            <CardTitle>Average Order Value</CardTitle>
            <CardDescription>Average invoice value over time</CardDescription>
          </div>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            <TimeSeriesTypeToggle value={seriesType} onChange={setSeriesType} />
            <div className="flex items-center gap-2 whitespace-nowrap text-[10px] text-slate-500">
              <span className="h-1.5 w-1.5 rounded-none" style={{ backgroundColor: chartLine.line }} />
              <span>{`AOV (${currencyCode})`}</span>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex flex-1 flex-col px-2.5 pb-2.5 pt-2">
        <div className="flex min-h-0 flex-1 flex-col space-y-2">
          {!isCompact && (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {summaryItems.map((item) => (
                <div key={item.label} className="border border-slate-200 bg-white/70 px-3 py-2 shadow-[0_1px_0_rgba(255,255,255,0.8)_inset]">
                  <div className="text-[9px] uppercase tracking-[0.18em] text-slate-500">{item.label}</div>
                  <div className="mt-1 flex items-baseline justify-between gap-2">
                    <span className="text-sm font-semibold tabular-nums text-[#201C35]">{item.value}</span>
                    <span className="text-[10px] text-slate-500">{item.detail}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {loading ? (
            <SkeletonChart compact={isCompact} />
          ) : rawPoints.length === 0 ? (
            <EmptyState compact={isCompact} />
          ) : error ? (
            <EmptyState compact={isCompact} />
          ) : (
            <>
              <TimeSeriesChart
                id="aov-trend-chart"
                rawPoints={rawPoints}
                valueKey="averageOrderValue"
                valueLabel="AOV"
                currencyCode={currencyCode}
                height={chartHeight}
                seriesType={seriesType}
                ariaLabel={`Average order value chart in ${currencyCode}`}
              />
              <div id="aov-trend-announcer" className="sr-only" aria-live="polite" />
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
